import type { PageHelp } from '../../help/types';

/**
 * Page guide for `/projects/:code/suggestions` — proposals other projects made for this project's
 * keys, which they link (spec §3.2 rule 11, §7.7, ADR 0006). The page is the package inbox's
 * component (`packages/SuggestionsPage.tsx`) with `scopeType="project"`, so the anchors are shared.
 */
export const projectSuggestionsHelp: PageHelp = {
  title: 'Suggestions',
  intro:
    'Wording proposed by other projects for keys of this project that they link. Accepting a proposal changes the value here and in every project that links the key, so each one is a decision for whoever translates this project.',
  sections: ['The inbox', 'Deciding', 'Decision dialog'],
  contexts: {
    'decision-dialog': 'Click “Accept” or “Reject” on an open suggestion.',
  },
  tips: [
    'A suggestion is created when someone edits a linked key from another project without being allowed to translate in this one: the edit is stored here instead of being written. Someone who translates here as well writes directly.',
    'Owners and translators of this project, and administrators, see the Accept and Reject buttons. Everyone else can read the suggestions, and the page says so above the list. A translator limited to some languages can decide only in those.',
  ],
  steps: [
    {
      selector: '[data-help="suggestions-status"]',
      title: 'Filter by status',
      body: 'Open is the inbox of undecided proposals. Accepted and Rejected keep closed suggestions readable, each with who decided it and when.',
      section: 'The inbox',
      i18n: {
        'de-AT': {
          title: 'Nach Status filtern',
          body: 'Open ist der Eingang der noch nicht entschiedenen Vorschläge. Accepted und Rejected halten abgeschlossene Vorschläge lesbar, jeweils mit der Person, die entschieden hat, und dem Zeitpunkt.',
        },
        'sr-Latn-RS': {
          title: 'Filtriranje po statusu',
          body: 'Open je prijemno sanduče predloga o kojima još nije odlučeno. Accepted i Rejected čuvaju zatvorene predloge čitljivim, uz to ko je i kada odlučio.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-item"]',
      title: 'Compare now and proposed',
      body: 'Each card is one cell: the key, its locale, plural form and context where they apply, the project it came from, and who proposed it when. “Project now” is what this project says today, “Proposed” is the other project’s wording. A proposal that only fits the application it came from is better rejected with a note — that project can use a key of its own instead of the link.',
      section: 'The inbox',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Aktuell und Vorschlag vergleichen',
          body: 'Jede Karte ist eine Zelle: der Schlüssel, seine Sprache, Pluralform und Kontext, wo zutreffend, das Projekt, aus dem er kommt, und wer ihn wann vorgeschlagen hat. „Project now“ ist der heutige Wert dieses Projekts, „Proposed“ die Formulierung des anderen Projekts. Einen Vorschlag, der nur zur Anwendung passt, aus der er kommt, lehnen Sie besser mit einer Notiz ab – dieses Projekt kann statt der Verknüpfung einen eigenen Schlüssel verwenden.',
        },
        'sr-Latn-RS': {
          title: 'Poređenje trenutnog i predloženog',
          body: 'Svaka kartica je jedna ćelija: ključ, njegov jezik, oblik množine i kontekst kada postoje, projekat iz kog je stigao, kao i ko ga je i kada predložio. „Project now“ je ono što ovaj projekat danas kaže, „Proposed“ je formulacija drugog projekta. Predlog koji odgovara samo aplikaciji iz koje je stigao bolje je odbiti uz napomenu – taj projekat umesto veze može da koristi sopstveni ključ.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-accept"]',
      title: 'Accept a proposal',
      body: 'Opens a confirmation with an optional note. On confirm, the proposed value becomes this project’s translation, and every project that links the key shows it after its next publish. It is stored as draft, like any other human edit.',
      section: 'Deciding',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschlag annehmen',
          body: 'Öffnet eine Bestätigung mit optionaler Notiz. Nach dem Bestätigen wird der vorgeschlagene Wert zur Übersetzung dieses Projekts, und jedes Projekt, das den Schlüssel verknüpft, zeigt ihn nach seinem nächsten Veröffentlichen. Er wird als draft gespeichert, wie jede andere Bearbeitung durch eine Person.',
        },
        'sr-Latn-RS': {
          title: 'Prihvatanje predloga',
          body: 'Otvara potvrdu sa opcionom napomenom. Posle potvrde predložena vrednost postaje prevod ovog projekta, a svaki projekat koji povezuje ključ prikazuje je posle svoje sledeće objave. Čuva se kao draft, kao i svaka druga izmena koju napravi osoba.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-reject"]',
      title: 'Reject a proposal',
      body: 'Opens a confirmation with an optional note. On confirm, the proposal is closed and nothing changes: this project, and every project that links the key, keeps what it already says. A note saying why spares the proposer the guessing.',
      section: 'Deciding',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschlag ablehnen',
          body: 'Öffnet eine Bestätigung mit optionaler Notiz. Nach dem Bestätigen wird der Vorschlag geschlossen und nichts ändert sich: Dieses Projekt und jedes Projekt, das den Schlüssel verknüpft, behält den bisherigen Wert. Eine Notiz mit dem Grund erspart der vorschlagenden Person das Rätseln.',
        },
        'sr-Latn-RS': {
          title: 'Odbijanje predloga',
          body: 'Otvara potvrdu sa opcionom napomenom. Posle potvrde predlog se zatvara i ništa se ne menja: ovaj projekat, kao i svaki projekat koji povezuje ključ, zadržava postojeću vrednost. Napomena sa razlogom poštedeće predlagača nagađanja.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-note"]',
      title: 'Explain the decision',
      body: 'Optional. The note is kept with the suggestion and shown on its card under Accepted or Rejected. When you write one, it replaces the note the proposer left; left empty, theirs stays.',
      section: 'Decision dialog',
      context: 'decision-dialog',
      i18n: {
        'de-AT': {
          title: 'Entscheidung begründen',
          body: 'Optional. Die Notiz bleibt beim Vorschlag und erscheint auf seiner Karte unter Accepted oder Rejected. Schreiben Sie eine, ersetzt sie die Notiz der vorschlagenden Person; bleibt das Feld leer, bleibt deren Notiz erhalten.',
        },
        'sr-Latn-RS': {
          title: 'Obrazloženje odluke',
          body: 'Opciono. Napomena ostaje uz predlog i prikazuje se na njegovoj kartici pod Accepted ili Rejected. Ako je napišete, ona zamenjuje napomenu predlagača; ako polje ostane prazno, njegova napomena ostaje.',
        },
      },
    },
    {
      selector: '[data-help="suggestions-confirm"]',
      title: 'Confirm the decision',
      body: 'Applies the decision and closes the dialog; Cancel closes it without deciding. A suggestion that someone else has decided in the meantime is refused rather than decided twice.',
      section: 'Decision dialog',
      context: 'decision-dialog',
      i18n: {
        'de-AT': {
          title: 'Entscheidung bestätigen',
          body: 'Wendet die Entscheidung an und schließt den Dialog; „Cancel“ schließt ihn, ohne zu entscheiden. Einen Vorschlag, über den inzwischen jemand anderer entschieden hat, lehnt der Server ab, statt ihn doppelt zu entscheiden.',
        },
        'sr-Latn-RS': {
          title: 'Potvrda odluke',
          body: 'Primenjuje odluku i zatvara dijalog; „Cancel“ ga zatvara bez odluke. Predlog o kome je u međuvremenu odlučio neko drugi server odbija umesto da se o njemu odluči dva puta.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Vorschläge',
      intro:
        'Formulierungen, die andere Projekte für Schlüssel dieses Projekts vorschlagen, die sie verknüpfen. Das Annehmen eines Vorschlags ändert den Wert hier und in jedem Projekt, das den Schlüssel verknüpft; jeder Vorschlag ist daher eine Entscheidung derer, die dieses Projekt übersetzen.',
      sections: {
        'The inbox': 'Der Eingang',
        Deciding: 'Entscheiden',
        'Decision dialog': 'Entscheidungsdialog',
      },
      contexts: {
        'decision-dialog': 'Klicken Sie bei einem offenen Vorschlag auf „Accept“ oder „Reject“.',
      },
      tips: [
        'Ein Vorschlag entsteht, wenn jemand einen verknüpften Schlüssel aus einem anderen Projekt heraus bearbeitet, ohne in diesem Projekt übersetzen zu dürfen: Die Bearbeitung landet hier, statt geschrieben zu werden. Wer auch hier übersetzt, schreibt direkt.',
        'Owner und Translator dieses Projekts sowie Administratoren sehen die Schaltflächen „Accept“ und „Reject“. Alle anderen können die Vorschläge lesen, und die Seite weist über der Liste darauf hin. Wer nur für einige Sprachen freigeschaltet ist, kann nur in diesen entscheiden.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Predlozi',
      intro:
        'Formulacije koje drugi projekti predlažu za ključeve ovog projekta koje povezuju. Prihvatanje predloga menja vrednost ovde i u svakom projektu koji povezuje ključ, pa je svaki predlog odluka onih koji prevode ovaj projekat.',
      sections: {
        'The inbox': 'Prijemno sanduče',
        Deciding: 'Odlučivanje',
        'Decision dialog': 'Dijalog odluke',
      },
      contexts: {
        'decision-dialog': 'Kliknite na „Accept“ ili „Reject“ kod otvorenog predloga.',
      },
      tips: [
        'Predlog nastaje kada neko izmeni povezan ključ iz drugog projekta, a ne sme da prevodi u ovom: izmena se čuva ovde umesto da bude upisana. Ko prevodi i ovde, upisuje direktno.',
        'Owneri i translatori ovog projekta, kao i administratori, vide dugmad „Accept“ i „Reject“. Svi ostali mogu da čitaju predloge, a stranica to navodi iznad liste. Translator ograničen na neke jezike može da odlučuje samo na njima.',
      ],
    },
  },
};
